import React, { useEffect } from 'react';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import branding from '../../config/branding';

import { BaseText, Container, Placeholder, Subtitle, Title } from './styles';

const Terms: React.FC = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  return (
    <>
      <Header />
      <Container>
        <BaseText>Atualizado em: Setembro de 2026</BaseText>
        <Title>Termos de Uso</Title>
        <BaseText>
          Estes Termos de Uso regulam a utilização da plataforma TRENZOX,
          composta pelo painel web de gestão, pelo aplicativo do aluno e pelo
          aplicativo da equipe (em conjunto, a &quot;Plataforma&quot;),
          disponibilizada por{' '}
          <Placeholder>
            Pedro Mesmer Aquino Kitzinger, pessoa física, em processo de
            formalização como MEI
          </Placeholder>{' '}
          (&quot;TRENZOX&quot;, &quot;nós&quot;). Ao criar uma conta ou utilizar
          a Plataforma, você declara que leu, compreendeu e concorda
          integralmente com estes Termos e com a nossa{' '}
          <a href="/privacy">Política de Privacidade</a>.
        </BaseText>
        <BaseText>
          Dados cadastrais completos da pessoa responsável (CPF, endereço) estão
          disponíveis mediante solicitação pelo e-mail{' '}
          <a href={`mailto:${branding.supportEmail}`}>
            {branding.supportEmail}
          </a>
          . Assim que a formalização for concluída — seja como MEI, seja por
          meio de pessoa jurídica constituída para administrar a marca TRENZOX —
          esta seção será atualizada com a razão social e o CNPJ
          correspondentes, sem prejuízo da validade destes Termos já aceitos até
          então.
        </BaseText>
        <BaseText>
          Se você está aceitando estes Termos em nome de uma academia, estúdio
          ou negócio, você declara ter poderes para vincular essa instituição,
          que passa a ser considerada parte destes Termos junto com você.
        </BaseText>

        <Subtitle>1. Descrição do serviço</Subtitle>
        <BaseText>
          A TRENZOX é um sistema de gestão para academias e estúdios,
          oferecendo, entre outras funcionalidades: cadastro e gerenciamento de
          alunos e equipe; criação e acompanhamento de fichas de treino;
          biblioteca de exercícios; controle financeiro e de pagamentos;
          relatórios de retenção e engajamento; e canal de comunicação entre
          instituição e alunos. As funcionalidades disponíveis variam conforme o
          plano contratado, conforme descrito na página de{' '}
          <a href={`${branding.siteUrl}/#precos`}>planos e preços</a>.
        </BaseText>
        <BaseText>
          Podemos alterar, adicionar ou remover funcionalidades da Plataforma a
          qualquer momento, visando melhorias no serviço, sem que isso
          represente redução das funcionalidades essenciais do plano já
          contratado sem aviso prévio.
        </BaseText>
        <BaseText>
          O aplicativo do aluno também está disponível no modo{' '}
          <strong>avulso (standalone)</strong>, gratuito e sem vínculo com
          nenhuma instituição, permitindo que qualquer pessoa crie e acompanhe
          seus próprios treinos de forma independente. O uso do aplicativo nesse
          modo também está sujeito integralmente a estes Termos.
        </BaseText>

        <Subtitle>2. Cadastro e conta</Subtitle>
        <BaseText>
          Para usar a Plataforma é necessário criar uma conta, informando dados
          verdadeiros, completos e atualizados. Você é responsável por manter a
          confidencialidade da sua senha e por todas as atividades realizadas na
          sua conta.
        </BaseText>
        <BaseText>
          A Plataforma é destinada a maiores de 18 anos. Contas de gestores e
          equipe (staff) exigem maioridade; alunos vinculados a uma instituição
          devem também ter no mínimo 18 anos, conforme descrito na nossa{' '}
          <a href="/privacy">Política de Privacidade</a>.
        </BaseText>
        <BaseText>
          Um mesmo usuário pode manter múltiplos vínculos simultâneos na
          Plataforma sob o mesmo login — por exemplo, sendo gestor em uma
          instituição, membro da equipe em outra e aluno em uma terceira. Cada
          vínculo é regido pelas permissões e pelo plano da respectiva
          instituição, independentemente dos demais vínculos da mesma pessoa.
        </BaseText>

        <Subtitle>3. Planos, teste grátis e cobrança</Subtitle>
        <BaseText>
          A contratação de um plano paga é feita pela própria instituição
          (academia/estúdio), sendo esta a responsável financeira pela
          assinatura. Novas academias têm direito a um período de teste gratuito
          de 15 dias, sem necessidade de cadastro de cartão de crédito, com{' '}
          <strong>
            acesso completo a todas as funcionalidades da Plataforma
          </strong>{' '}
          durante o período de teste, independentemente do plano que venha a ser
          contratado ao final dele.
        </BaseText>
        <BaseText>
          A cobrança das assinaturas é processada por meio de provedor de
          pagamentos terceirizado (ex.: Mercado Pago), em ciclo mensal, com
          renovação automática ao final de cada ciclo. O não pagamento na data
          de vencimento pode resultar em até 5 (cinco) dias corridos de carência
          para regularização, seguidos de suspensão do acesso da instituição e
          de seus usuários até a quitação do débito.
        </BaseText>
        <BaseText>
          O cancelamento da assinatura pode ser feito a qualquer momento pela
          própria instituição, dentro da Plataforma ou pelo e-mail{' '}
          <a href={`mailto:${branding.supportEmail}`}>
            {branding.supportEmail}
          </a>
          . Não haverá reembolso ou estorno proporcional de valores para
          cancelamentos solicitados no decorrer de um ciclo mensal já pago,
          sendo garantido o acesso à Plataforma até o término da vigência paga.
        </BaseText>
        <BaseText>
          Eventuais reajustes de preço serão comunicados à instituição com no
          mínimo 30 (trinta) dias de antecedência antes de entrarem em vigor na
          próxima cobrança.
        </BaseText>
        <BaseText>
          Aumentos de limites do plano contratado (como quantidade de alunos,
          armazenamento de mídias ou avisos) podem ser solicitados a qualquer
          momento diretamente ao nosso suporte, pelo e-mail{' '}
          <a href={`mailto:${branding.supportEmail}`}>
            {branding.supportEmail}
          </a>
          , e são concedidos mediante cobrança adicional, quando aplicável.
        </BaseText>

        <Subtitle>4. Uso aceitável</Subtitle>
        <BaseText>Ao utilizar a Plataforma, você concorda em não:</BaseText>
        <BaseText>
          <ul>
            <li>Fornecer informações falsas, incompletas ou enganosas;</li>
            <li>
              Usar a Plataforma para fins ilícitos ou que violem direitos de
              terceiros;
            </li>
            <li>
              Tentar acessar áreas, dados ou contas de terceiros sem
              autorização;
            </li>
            <li>
              Realizar engenharia reversa, copiar, revender ou explorar
              comercialmente a Plataforma fora do uso pretendido;
            </li>
            <li>
              Sobrecarregar, interferir ou comprometer a segurança e o
              funcionamento normal dos nossos sistemas.
            </li>
          </ul>
        </BaseText>
        <BaseText>
          O descumprimento destas regras pode resultar em suspensão ou
          encerramento da conta, sem prejuízo de outras medidas cabíveis.
        </BaseText>

        <Subtitle>5. Conteúdo e propriedade intelectual</Subtitle>
        <BaseText>
          A marca TRENZOX, o software, o design e a biblioteca nativa de
          exercícios são de propriedade da TRENZOX (ou licenciados a ela), sendo
          protegidos por leis de propriedade intelectual. Nenhuma disposição
          destes Termos concede a você direitos sobre essas propriedades além do
          necessário para uso normal da Plataforma durante a vigência da sua
          assinatura.
        </BaseText>
        <BaseText>
          Conteúdo enviado pela instituição ou por usuários (como logo,
          exercícios customizados, fotos e vídeos de execução, mensagens)
          continua de propriedade de quem o enviou. Ao enviá-lo, você concede à
          TRENZOX uma licença para armazenar, processar e exibir esse conteúdo
          dentro da Plataforma, exclusivamente para viabilizar o funcionamento
          do serviço. Você declara possuir os direitos necessários sobre
          qualquer conteúdo que enviar.
        </BaseText>
        <BaseText>
          A TRENZOX se reserva o direito de otimizar, comprimir, modificar ou
          remover mídias da biblioteca nativa de exercícios, bem como mídias
          enviadas por usuários (fotos, logotipos e vídeos), a qualquer momento,
          visando a manutenção da infraestrutura, o desempenho, a segurança ou a
          adequação do sistema.
        </BaseText>

        <Subtitle>6. Disponibilidade do serviço</Subtitle>
        <BaseText>
          Envidamos esforços para manter a Plataforma disponível de forma
          contínua, porém não garantimos operação ininterrupta ou livre de
          falhas. Podem ocorrer interrupções programadas para manutenção (com
          aviso prévio quando possível) ou eventuais indisponibilidades
          decorrentes de fatores fora do nosso controle razoável.
        </BaseText>

        <Subtitle>7. Limitação de responsabilidade</Subtitle>
        <BaseText>
          Na máxima extensão permitida pela legislação aplicável, a TRENZOX não
          será responsável por danos indiretos, lucros cessantes ou perda de
          dados decorrentes do uso ou da impossibilidade de uso da Plataforma,
          exceto nos casos de dolo ou culpa grave. A Plataforma é uma ferramenta
          de apoio à gestão e prescrição de treinos; a responsabilidade técnica
          pela adequação de cada treino ao aluno permanece do profissional que o
          prescreveu.
        </BaseText>

        <Subtitle>8. Rescisão</Subtitle>
        <BaseText>
          Estes Termos vigoram enquanto durar sua conta ativa na Plataforma.
          Você (ou a instituição) pode encerrar a conta a qualquer momento.
          Podemos suspender ou encerrar contas que violem estes Termos, a
          legislação aplicável, ou em caso de inadimplência não regularizada
          conforme a seção 3.
        </BaseText>
        <BaseText>
          <strong>Contas pendentes ou não ativadas:</strong> cadastros de
          instituições ou usuários que, nos primeiros 30 (trinta) dias após o
          cadastro, não validarem o e-mail, não realizarem login, não
          demonstrarem uso efetivo da Plataforma, ou não converterem para um
          plano pago ao final do período de teste de 15 dias, poderão ser
          excluídos definitivamente pela TRENZOX, junto com todos os seus dados,
          independentemente de aviso prévio.
        </BaseText>
        <BaseText>
          <strong>Contas inativas:</strong> a TRENZOX poderá encerrar e excluir
          contas que permanecerem sem qualquer acesso ou uso registrado por
          período superior a 90 (noventa) dias. Quando aplicável, o usuário será
          notificado pelo e-mail cadastrado e terá prazo de até 7 (sete) dias
          para fazer login e reativar a conta antes da exclusão definitiva.
        </BaseText>
        <BaseText>
          <strong>Retenção após encerramento:</strong> o encerramento da
          assinatura, por si só, não exclui os dados da instituição — eles
          permanecem acessíveis, sujeitos às limitações de um plano sem
          assinatura ativa. Já a exclusão definitiva da conta (institucional ou
          de usuário), quando solicitada ou executada por qualquer uma das
          partes, é <strong>imediata e irrecuperável</strong>: os dados são
          removidos de nossos servidores assim que a exclusão é processada, sem
          período de retenção posterior, salvo obrigações legais de guarda. Por
          isso, recomendamos exportar os dados relevantes antes de solicitar a
          exclusão — não será possível recuperá-los depois. O tratamento de
          dados pessoais segue nossa{' '}
          <a href="/privacy">Política de Privacidade</a>.
        </BaseText>
        <BaseText strong>
          A TRENZOX se reserva o direito de descontinuar a Plataforma como um
          todo a qualquer momento, mediante aviso prévio de no mínimo 30
          (trinta) dias às instituições ativas, por e-mail e/ou comunicado na
          própria Plataforma, para que possam exportar seus dados antes do
          encerramento definitivo do serviço.
        </BaseText>

        <Subtitle>9. Alterações destes Termos</Subtitle>
        <BaseText>
          Podemos atualizar estes Termos periodicamente. A versão vigente estará
          sempre disponível nesta página, com a data da última atualização
          indicada no topo. O uso continuado da Plataforma após uma atualização
          implica concordância com os novos Termos.
        </BaseText>

        <Subtitle>10. Legislação aplicável e foro</Subtitle>
        <BaseText>
          Estes Termos são regidos pelas leis da República Federativa do Brasil.
          Fica eleito o foro da comarca de <Placeholder>NATAL/RN</Placeholder>{' '}
          para dirimir quaisquer controvérsias decorrentes destes Termos, com
          renúncia a qualquer outro, por mais privilegiado que seja.
        </BaseText>

        <Subtitle>11. Contato</Subtitle>
        <BaseText>
          Dúvidas sobre estes Termos podem ser enviadas para{' '}
          <a href={`mailto:${branding.supportEmail}`}>
            {branding.supportEmail}
          </a>
          . Responderemos com a maior brevidade possível.
        </BaseText>
      </Container>
      <Footer />
    </>
  );
};

export { Terms };
